import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export type Mode = "light" | "dark" | "system";
export type Preset = "lab" | "warm" | "weiss" | "custom";

export interface PresetInfo {
  id: Preset;
  /** Mini-Preview-Swatches: [Canvas, Akzent, Text/Dunkel] */
  swatches: [string, string, string];
}

/** Registry der „vollen" CSS-Block-Presets (jeweils ein Block in themes.css).
 *  Das „Eigene" (custom) Preset wird in den Einstellungen separat behandelt,
 *  weil es Regler statt eines statischen Blocks hat. */
export const PRESETS: PresetInfo[] = [
  { id: "lab", swatches: ["#ffffff", "#217e91", "#1a1d1f"] },
  { id: "warm", swatches: ["#faf9f5", "#C15F3C", "#2c2c2c"] },
  { id: "weiss", swatches: ["#ffffff", "#217e91", "#1a1d1f"] },
];

// -------------------------------------------------------------- Custom-Theme

export type BorderStrength = "normal" | "strong";

export interface CustomTheme {
  /** Akzent-Basisfarbe (Hex), aus der die 50–900-Rampe berechnet wird. */
  accent: string;
  /** 0 = reinweiß, 1 = leicht getönt (250 250 248). */
  canvasTint: number;
  borderStrength: BorderStrength;
}

export const CUSTOM_DEFAULT: CustomTheme = {
  accent: "#217e91",
  canvasTint: 0,
  borderStrength: "normal",
};

const CUSTOM_KEY = "sb_theme_custom";

function getInitialCustom(): CustomTheme {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        accent: typeof p.accent === "string" ? p.accent : CUSTOM_DEFAULT.accent,
        canvasTint:
          typeof p.canvasTint === "number"
            ? Math.min(1, Math.max(0, p.canvasTint))
            : CUSTOM_DEFAULT.canvasTint,
        borderStrength:
          p.borderStrength === "strong" ? "strong" : "normal",
      };
    }
  } catch {
    /* ignore */
  }
  return { ...CUSTOM_DEFAULT };
}

// --- Akzent-Rampen-Algorithmus (Hex → HSL → Lightness-Leiter) --------------
// Leiter an die bestehende Lab-Rampe justiert (Dry-Run 2026-07-08):
//   Petrol #217e91 → 500 trifft #217e91 (Kontrast 4.71 ≥ 4:1),
//   Blau #2563eb → 500 Kontrast 9.47, Rot #b91c1c → 500 Kontrast 8.30,
//   je 50er L≈96 > 95.  Prompt-Vorschlag [97..17] war für Cyan zu hell
//   (Petrol-500 fiel auf 2.65) → auf Lab-L-Leiter korrigiert.
const RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
const L_LADDER = [96, 91, 81, 69, 50, 35, 28, 23, 18, 13];
const SAT_FACTOR = [0.72, 0.74, 0.74, 0.72, 0.75, 1, 1.06, 1.08, 1, 0.93];

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

/** Berechnet die Akzent-Rampe als CSS-Triplet-Map { "accent-500": "r g b", … }. */
export function accentRamp(hex: string): Record<string, string> {
  const [h, s] = rgbToHsl(...hexToRgb(hex));
  const out: Record<string, string> = {};
  RAMP_STEPS.forEach((step, i) => {
    const [r, g, b] = hslToRgb(
      h,
      Math.min(100, s * SAT_FACTOR[i]),
      L_LADDER[i],
    );
    out[`accent-${step}`] = `${r} ${g} ${b}`;
  });
  return out;
}

/** Baut das CSS für das injizierte <style id="custom-theme">. */
function buildCustomCss(c: CustomTheme): string {
  const ramp = accentRamp(c.accent);
  const rampDecl = Object.entries(ramp)
    .map(([k, v]) => `  --${k}: ${v};`)
    .join("\n");

  // Canvas-Ton: linear reinweiß (255 255 255) ↔ getönt (250 250 248).
  const t = c.canvasTint;
  const cr = Math.round(255 - 5 * t);
  const cg = Math.round(255 - 5 * t);
  const cb = Math.round(255 - 7 * t);
  const canvas = `${cr} ${cg} ${cb}`;
  // Reinweiß (t=0): Canvas, Surface UND Surface-2 (Sidebar/Wells) exakt 255.
  // Nur so wirkt „Reinweiß" auch auf der Sidebar reinweiß — sonst blieb
  // surface-2 auf dem Lab-Grauton 247 248 249 hängen und brach den Eindruck.
  // Getönt (t>0): Surface-2 einen Hauch dunkler als der getönte Canvas.
  const surface = t > 0 ? "255 255 255" : "255 255 255";
  const surface2 = t > 0 ? `${cr - 3} ${cg - 3} ${cb - 3}` : "255 255 255";
  // Surface-3 bleibt Hover-Grau (Reinweiß minimal, getönt etwas kräftiger).
  const surface3 = t > 0 ? `${cr - 11} ${cg - 11} ${cb - 11}` : "244 246 247";

  // Linien-Stärke.
  const border = c.borderStrength === "strong" ? "213 217 222" : "229 231 234";
  const borderStrong =
    c.borderStrength === "strong" ? "188 194 201" : "209 213 218";

  // Light-Overrides (höhere Spezifität als Lab-Basis via `html[data-theme]`).
  const light = `html[data-theme="lab"] {
${rampDecl}
  --canvas: ${canvas};
  --surface: ${surface};
  --surface-2: ${surface2};
  --surface-3: ${surface3};
  --border: ${border};
  --border-strong: ${borderStrong};
  --run: var(--accent-500); --run-soft: var(--accent-50);
}`;

  // Dark-Overrides: nur der Akzent folgt der Wahl (Flächen bleiben Lab-Dark).
  const dark = `html.dark[data-theme="lab"] {
${rampDecl}
  --run: var(--accent-500);
}`;

  return `${light}\n${dark}\n`;
}

const CUSTOM_STYLE_ID = "custom-theme";

function applyCustomStyle(c: CustomTheme | null) {
  let el = document.getElementById(CUSTOM_STYLE_ID) as HTMLStyleElement | null;
  if (!c) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("style");
    el.id = CUSTOM_STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = buildCustomCss(c);
}

// -------------------------------------------------------------- Provider

interface ThemeCtx {
  /** Gewählter Modus (kann "system" sein). */
  mode: Mode;
  /** Aufgelöster Modus für die tatsächliche Darstellung. */
  effective: "light" | "dark";
  preset: Preset;
  custom: CustomTheme;
  setMode: (m: Mode) => void;
  setPreset: (p: Preset) => void;
  setCustom: (c: CustomTheme) => void;
  /** Kompat: schaltet zwischen hell/dunkel (auf Basis des effektiven Modus). */
  toggle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

const MODE_KEY = "sb_theme_mode";
const PRESET_KEY = "sb_theme_preset";
const LEGACY_MODE_KEY = "sb_theme"; // vor P1a

function systemPrefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function getInitialMode(): Mode {
  const saved = localStorage.getItem(MODE_KEY);
  if (saved === "light" || saved === "dark" || saved === "system") return saved;
  // Migration des alten Keys (nur "light"/"dark" möglich).
  const legacy = localStorage.getItem(LEGACY_MODE_KEY);
  if (legacy === "light" || legacy === "dark") {
    localStorage.setItem(MODE_KEY, legacy);
    return legacy;
  }
  return "system";
}

function getInitialPreset(): Preset {
  const saved = localStorage.getItem(PRESET_KEY);
  if (saved === "lab" || saved === "warm" || saved === "weiss" || saved === "custom")
    return saved;
  return "lab";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(getInitialMode);
  const [preset, setPresetState] = useState<Preset>(getInitialPreset);
  const [custom, setCustomState] = useState<CustomTheme>(getInitialCustom);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Auf OS-Präferenz hören (nur relevant im "system"-Modus).
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const effective: "light" | "dark" =
    mode === "system" ? (systemDark ? "dark" : "light") : mode;

  // Attribute + Klasse auf <html> setzen. Bei „custom" ist die Basis Lab
  // (data-theme="lab") + injiziertes <style id="custom-theme">.
  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute("data-theme", preset === "custom" ? "lab" : preset);
    el.classList.toggle("dark", effective === "dark");
  }, [preset, effective]);

  // Custom-Style injizieren/entfernen je nach Preset + Werten.
  useEffect(() => {
    applyCustomStyle(preset === "custom" ? custom : null);
  }, [preset, custom]);

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(PRESET_KEY, preset);
  }, [preset]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(custom));
  }, [custom]);

  const setMode = useCallback((m: Mode) => setModeState(m), []);
  const setPreset = useCallback((p: Preset) => setPresetState(p), []);
  const setCustom = useCallback((c: CustomTheme) => setCustomState(c), []);
  const toggle = useCallback(() => {
    setModeState((m) => {
      const eff = m === "system" ? (systemPrefersDark() ? "dark" : "light") : m;
      return eff === "dark" ? "light" : "dark";
    });
  }, []);

  return (
    <Ctx.Provider
      value={{ mode, effective, preset, custom, setMode, setPreset, setCustom, toggle }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme outside provider");
  return v;
}
